import { motion } from "framer-motion";
import { Clock3, Tag } from "lucide-react";
import { waLink, WhatsAppIcon } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";
import { WeatherChip } from "@/components/WeatherBadge";

const IMAGES = {
  hallstatt_tour: "/images/hallstatt-lake.webp",
  koenigssee: "/images/koenigssee.webp",
  zell_kaprun: "/images/zell-glacier.webp",
  werfen: "/images/hohenwerfen.webp",
  sound_of_music: "/images/salzburg-mirabell.webp",
  munich: "/images/munich.webp",
  vienna: "/images/vienna.webp",
  innsbruck: "/images/innsbruck.webp",
  venice: "/images/venice.webp",
};

const WEATHER_PLACE = {
  hallstatt: "hallstatt",
  koenigssee: "koenigssee",
  zellkaprun: "zell",
  werfen: "werfen",
  soundofmusic: "salzburg",
  munich: "munich",
  vienna: "vienna",
  innsbruck: "innsbruck",
  venice: "venice",
};

export default function Tours() {
  const { lang } = useLang();
  const t = T[lang].tours;

  return (
    <section id="tours" data-testid="tours-section" className="relative py-24 lg:py-36 bg-[#201B16] border-y border-stone-800/80 overflow-hidden">
      <img src="/images/tours-bg.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#16110C]/40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#201B16] via-[#201B16]/20 to-[#201B16]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>
          </h2>
          <p className="mt-5 text-stone-400 leading-relaxed">{t.sub}</p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.items.map((tour, i) => (
            <motion.article
              key={tour.testid}
              data-testid={`tour-card-${tour.testid}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: "easeOut" }}
              whileHover={{ y: -8, scale: 1.03 }}
              className="group rounded-3xl overflow-hidden bg-[#2E2720] border border-stone-800 shadow-sm hover:shadow-xl hover:shadow-black/50 hover:border-[#C5A059]/25 transition-all duration-500 flex flex-col"
            >
              <div className="relative overflow-hidden aspect-[3/2]">
                <img
                  src={IMAGES[tour.img]}
                  alt={tour.name}
                  loading="lazy" decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-wide text-stone-100 border border-white/20">
                  <Clock3 className="w-3.5 h-3.5 text-[#C5A059]" />
                  {tour.duration}
                </span>
                <div className="absolute top-4 right-4">
                  <WeatherChip place={WEATHER_PLACE[tour.testid]} />
                </div>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-serif-display font-semibold text-2xl text-stone-100">{tour.name}</h3>
                <p className="mt-2.5 text-sm text-stone-400 leading-relaxed flex-1">{tour.highlights}</p>
                <div className="mt-6 flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-[#C5A059]">
                    <Tag className="w-3.5 h-3.5" />
                    {t.priceNote}
                  </span>
                  <a
                    data-testid={`tour-enquire-${tour.testid}`}
                    href={waLink(tour.wa)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-[#C5A059] text-[#16110C] px-4 py-2.5 text-xs font-semibold hover:bg-[#25D366] hover:text-white transition-colors duration-300"
                  >
                    <WhatsAppIcon className="w-3.5 h-3.5" />
                    {t.cta}
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
